// SPDX-License-Identifier: AGPL-3.0-or-later

import {SettingsRow} from '@app/features/app/components/dialogs/shared/SettingsRow';
import {openFilePicker} from '@app/features/messaging/utils/FilePickerUtils';
import * as GlassThemeCommands from '@app/features/theme/commands/GlassThemeCommands';
import type {
	GlassBackdropStyle,
	GlassLook,
	GlassPalette,
	GlassPreset,
	GlassTone,
	GlassWallpaperLayout,
} from '@app/features/theme/state/GlassTheme';
import GlassTheme, {
	GLASS_DEFAULT_SETTINGS,
	GLASS_LIMITS,
	GLASS_LOOK_VALUES,
	GLASS_PALETTE_COLORS,
	GlassBackdropStyles,
	GlassLooks,
	GlassPalettes,
	GlassPresets,
	GlassTones,
	GlassWallpaperLayouts,
} from '@app/features/theme/state/GlassTheme';
import GlassWallpaperImage from '@app/features/theme/state/GlassWallpaperImage';
import {isGlassThemeActive} from '@app/features/theme/utils/GlassThemeUtils';
import {Button} from '@app/features/ui/button/Button';
import {Switch} from '@app/features/ui/components/form/FormSwitch';
import {RESET_SLIDER_TO_DEFAULT_VALUE_DESCRIPTOR, Slider} from '@app/features/ui/components/Slider';
import {canResetSliderValue, SliderResetIconButton} from '@app/features/ui/components/slider/SliderResetIconButton';
import {
	DesktopIcon,
	ImageIcon,
	MoonIcon,
	PaintBrushIcon,
	SidebarSimpleIcon,
	SlidersHorizontalIcon,
	SunIcon,
} from '@app/features/ui/icons/Icons';
import {Segmented} from '@app/features/ui/konvo_controls/KonvoControls';
import type {RadioOption} from '@app/features/ui/radio_group/RadioGroup';
import {RadioGroup} from '@app/features/ui/radio_group/RadioGroup';
import {formatRoundedPercentage} from '@app/features/ui/utils/PercentageFormatting';
import {ChoiceCard, choiceCardStyles} from '@app/features/user/components/modals/tabs/appearance_tab/cards/ChoiceCard';
import {ThemePreviewArt} from '@app/features/user/components/modals/tabs/appearance_tab/cards/ThemePreviewArt';
import styles from '@app/features/user/components/modals/tabs/appearance_tab/GlassTab.module.css';
import type {I18n, MessageDescriptor} from '@lingui/core';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {clsx} from 'clsx';
import {action, observable} from 'mobx';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useEffect} from 'react';

const GLASS_INACTIVE_NOTE_DESCRIPTOR = msg({
	message:
		'Pick the Glass theme above to choose light or dark, and to tune blur, transparency and the wallpaper behind the app.',
	comment: 'Note shown in the glass effect settings when a different theme is selected.',
});
const LOOKS_DESCRIPTOR = msg({
	message: 'Looks',
	comment: 'Heading for the one-click glass looks. Keep it concise.',
});
const LAYOUT_DESCRIPTOR = msg({
	message: 'Glow layout',
	comment: 'Heading for where the wallpaper colours sit. Keep it concise.',
});
const LAYOUT_CORNERS_DESCRIPTOR = msg({message: 'Corners', comment: 'Glow layout option. Keep it concise.'});
const LAYOUT_CORNERS_DESCRIPTION_DESCRIPTOR = msg({
	message: 'Top left, top right and bottom centre.',
	comment: 'Description of the corners glow layout.',
});
const LAYOUT_STUDIO_DESCRIPTOR = msg({message: 'Studio', comment: 'Glow layout option. Keep it concise.'});
const LAYOUT_STUDIO_DESCRIPTION_DESCRIPTOR = msg({
	message: 'Left, bottom right and top right, like the concept boards.',
	comment: 'Description of the studio glow layout.',
});
const LAYOUT_HORIZON_DESCRIPTOR = msg({message: 'Horizon', comment: 'Glow layout option. Keep it concise.'});
const LAYOUT_HORIZON_DESCRIPTION_DESCRIPTOR = msg({
	message: 'A band along the top and one along the bottom.',
	comment: 'Description of the horizon glow layout.',
});
const LAYOUT_CENTRE_DESCRIPTOR = msg({message: 'Centre', comment: 'Glow layout option. Keep it concise.'});
const LAYOUT_CENTRE_DESCRIPTION_DESCRIPTOR = msg({
	message: 'Gathered in the middle, edges stay quiet.',
	comment: 'Description of the centre glow layout.',
});
const GRAIN_DESCRIPTOR = msg({
	message: 'Grain',
	comment: 'Slider label: film grain over the wallpaper. Keep it concise.',
});
const GRAIN_DESCRIPTION_DESCRIPTOR = msg({
	message: 'A fine texture over the wallpaper so gradients do not band.',
	comment: 'Description under the glass wallpaper grain slider.',
});
const VIGNETTE_DESCRIPTOR = msg({
	message: 'Vignette',
	comment: 'Slider label: darkened wallpaper edges. Keep it concise.',
});
const VIGNETTE_DESCRIPTION_DESCRIPTOR = msg({
	message: 'Darkens the edges so the panels sit in a pool of light.',
	comment: 'Description under the glass wallpaper vignette slider.',
});
const MATCH_SYSTEM_DESCRIPTOR = msg({
	message: 'Match system',
	comment: 'Glass tone option that follows the OS light/dark setting. Keep it concise.',
});
const LIGHT_LOOK_DESCRIPTOR = msg({
	message: 'Light look',
	comment: 'Heading above the light glass looks when the tone matches the system.',
});
const DARK_LOOK_DESCRIPTOR = msg({
	message: 'Dark look',
	comment: 'Heading above the dark glass looks when the tone matches the system.',
});
const CUSTOMISE_DESCRIPTOR = msg({
	message: 'Customise wallpaper',
	comment: 'Small button beside the Glass heading that shows the detailed wallpaper and panel controls.',
});
const HIDE_CUSTOMISE_DESCRIPTOR = msg({
	message: 'Hide customisation',
	comment: 'Button that hides the detailed glass controls.',
});
const LOOK_MIST_MONO_DESCRIPTOR = msg({
	message: 'Mist mono',
	comment: 'Glass look name (light): frosted panels over a quiet grey wallpaper.',
});
const LOOK_MIST_MONO_DESCRIPTION_DESCRIPTOR = msg({
	message: 'Quiet grey',
	comment: 'Short description of the Mist mono glass look.',
});
const LOOK_MIST_STUDIO_DESCRIPTOR = msg({
	message: 'Mist studio',
	comment: 'Glass look name (light): frosted panels over soft lavender, teal and peach.',
});
const LOOK_MIST_STUDIO_DESCRIPTION_DESCRIPTOR = msg({
	message: 'Lavender, teal and peach',
	comment: 'Short description of the Mist studio glass look.',
});
const LOOK_MIST_KONVO_DESCRIPTOR = msg({
	message: 'Mist Konvo',
	comment: 'Glass look name (light): frosted panels over soft violet, pink and blue.',
});
const LOOK_MIST_KONVO_DESCRIPTION_DESCRIPTOR = msg({
	message: 'Violet, pink and blue',
	comment: 'Short description of the Mist Konvo glass look.',
});
const LOOK_SMOKED_GRAPHITE_DESCRIPTOR = msg({
	message: 'Smoked graphite',
	comment: 'Glass look name (dark): graphite glass over a near-black wallpaper.',
});
const LOOK_SMOKED_GRAPHITE_DESCRIPTION_DESCRIPTOR = msg({
	message: 'Near-black, calm',
	comment: 'Short description of the Smoked graphite glass look.',
});
const LOOK_SMOKED_STUDIO_DESCRIPTOR = msg({
	message: 'Smoked studio',
	comment: 'Glass look name (dark): graphite glass over soft colour glows.',
});
const LOOK_SMOKED_STUDIO_DESCRIPTION_DESCRIPTOR = msg({
	message: 'Soft colour glows',
	comment: 'Short description of the Smoked studio glass look.',
});
const LOOK_PHOTO_DESCRIPTION_DESCRIPTOR = msg({
	message: 'Any image as the wallpaper',
	comment: 'Short description of the Your photo glass look card.',
});
const CHOOSE_A_PHOTO_DESCRIPTOR = msg({
	message: 'Choose a photo',
	comment: 'Placeholder inside the Your photo glass look card before a picture is picked.',
});
const FINE_TUNE_BLUR_DESCRIPTION_DESCRIPTOR = msg({
	message: 'How much of the wallpaper shows through',
	comment: 'One-line description of the Blur row in the Glass fine-tune settings.',
});
const FINE_TUNE_FLOATING_DESCRIPTION_DESCRIPTOR = msg({
	message: 'Rounded cards with space around them',
	comment: 'One-line description of the Floating panels row in the Glass fine-tune settings.',
});
const FINE_TUNE_REDUCE_DESCRIPTION_DESCRIPTOR = msg({
	message: 'Solid panels, no blur',
	comment: 'One-line description of the Reduce transparency row in the Glass fine-tune settings.',
});
const TONE_DESCRIPTOR = msg({
	message: 'Appearance',
	comment: 'Heading for the glass theme light/dark/system choice. Keep it concise.',
});
const TONE_DARK_DESCRIPTOR = msg({message: 'Dark', comment: 'Glass tone option. Keep it concise.'});
const TONE_LIGHT_DESCRIPTOR = msg({message: 'Light', comment: 'Glass tone option. Keep it concise.'});
const COLOURS_DESCRIPTOR = msg({
	message: 'Wallpaper colours',
	comment: 'Heading for the glass wallpaper colour presets and pickers. Keep it concise.',
});
const COLOURS_DESCRIPTION_DESCRIPTOR = msg({
	message: 'Pick a set or choose your own three colours. They tint every wallpaper style above.',
	comment: 'Description under the glass wallpaper colour controls.',
});
const PALETTE_KONVO_DESCRIPTOR = msg({message: 'Konvo', comment: 'Glass wallpaper colour set: violet, magenta, cyan.'});
const PALETTE_SUNRISE_DESCRIPTOR = msg({
	message: 'Sunrise',
	comment: 'Glass wallpaper colour set: orange, purple, blue.',
});
const PALETTE_OCEAN_DESCRIPTOR = msg({message: 'Ocean', comment: 'Glass wallpaper colour set: teal and blues.'});
const PALETTE_FOREST_DESCRIPTOR = msg({message: 'Forest', comment: 'Glass wallpaper colour set: greens.'});
const PALETTE_EMBER_DESCRIPTOR = msg({message: 'Ember', comment: 'Glass wallpaper colour set: reds and amber.'});
const PALETTE_MONO_DESCRIPTOR = msg({message: 'Mono', comment: 'Glass wallpaper colour set: greys.'});
const COLOUR_PICKER_DESCRIPTOR = msg({
	message: 'Colour {index}',
	comment: 'Accessible label of a glass wallpaper colour picker. {index} is 1, 2 or 3.',
});
const INTENSITY_DESCRIPTOR = msg({
	message: 'Colour strength',
	comment: 'Slider label: how strongly the wallpaper colours show. Keep it concise.',
});
const INTENSITY_DESCRIPTION_DESCRIPTOR = msg({
	message: 'How much of the colour shows. Zero leaves a plain canvas.',
	comment: 'Description under the glass wallpaper colour strength slider.',
});
const SOFTNESS_DESCRIPTOR = msg({
	message: 'Softness',
	comment: 'Slider label: how far the wallpaper colours are smeared. Keep it concise.',
});
const SOFTNESS_DESCRIPTION_DESCRIPTOR = msg({
	message: 'Blurs the wallpaper itself. Higher values melt the glows into each other.',
	comment: 'Description under the glass wallpaper softness slider.',
});
const BRIGHTNESS_DESCRIPTOR = msg({
	message: 'Brightness',
	comment: 'Slider label: how light or dark the wallpaper canvas is. Keep it concise.',
});
const BRIGHTNESS_DESCRIPTION_DESCRIPTOR = msg({
	message: 'Lightens or darkens the canvas behind the colours.',
	comment: 'Description under the glass wallpaper brightness slider.',
});
const PRESET_DESCRIPTOR = msg({
	message: 'Preset',
	comment: 'Short label for a row of glass theme preset buttons. Keep it concise.',
});
const SMOKED_DESCRIPTOR = msg({
	message: 'Smoked',
	comment: 'Glass theme preset name: dark, heavily blurred panels. Keep it concise.',
});
const BALANCED_DESCRIPTOR = msg({
	message: 'Balanced',
	comment: 'Glass theme preset name: medium blur and transparency. Keep it concise.',
});
const SUBTLE_DESCRIPTOR = msg({
	message: 'Subtle',
	comment: 'Glass theme preset name: mostly opaque panels with a light blur. Keep it concise.',
});
const FROSTED_DESCRIPTOR = msg({
	message: 'Frosted',
	comment: 'Glass theme preset name: strong blur with bright edges. Keep it concise.',
});
const CLEAR_DESCRIPTOR = msg({
	message: 'Clear',
	comment: 'Glass theme preset name: very transparent panels. Keep it concise.',
});
const BLUR_DESCRIPTOR = msg({
	message: 'Blur',
	comment: 'Slider label for the glass theme backdrop blur radius. Keep it concise.',
});
const PANEL_OPACITY_DESCRIPTOR = msg({
	message: 'Panel opacity',
	comment: 'Slider label for the glass theme panel tint opacity. Keep it concise.',
});
const PANEL_OPACITY_DESCRIPTION_DESCRIPTOR = msg({
	message: 'Lower values let more of the wallpaper through. Raise it if text becomes hard to read.',
	comment: 'Description under the glass theme panel opacity slider.',
});
const EDGE_GLOW_DESCRIPTOR = msg({
	message: 'Edge glow',
	comment: 'Slider label for the brightness of glass panel borders. Keep it concise.',
});
const CORNER_RADIUS_DESCRIPTOR = msg({
	message: 'Corner radius',
	comment: 'Slider label for the corner rounding of glass panels, tiles and rows. Keep it concise.',
});
const CORNER_RADIUS_DESCRIPTION_DESCRIPTOR = msg({
	message: 'Rounds panels, the user pill, list rows and rounded rail icons.',
	comment: 'Description under the glass theme corner radius slider.',
});
const WALLPAPER_DESCRIPTOR = msg({
	message: 'Wallpaper',
	comment: 'Heading for the glass theme backdrop choice. Keep it concise.',
});
const AURORA_DESCRIPTOR = msg({
	message: 'Aurora',
	comment: 'Glass theme wallpaper option: soft coloured glows. Keep it concise.',
});
const AURORA_DESCRIPTION_DESCRIPTOR = msg({
	message: 'Soft glows in your three colours.',
	comment: 'Description of the aurora glass wallpaper.',
});
const MESH_DESCRIPTOR = msg({
	message: 'Mesh',
	comment: 'Glass theme wallpaper option: a smooth colour gradient. Keep it concise.',
});
const MESH_DESCRIPTION_DESCRIPTOR = msg({
	message: 'A smooth multi-colour gradient.',
	comment: 'Description of the mesh glass wallpaper.',
});
const SCENE_DESCRIPTOR = msg({
	message: 'Scene',
	comment: 'Glass theme wallpaper option: an illustrated night scene. Keep it concise.',
});
const SCENE_DESCRIPTION_DESCRIPTOR = msg({
	message: 'An illustrated scene tinted with your colours.',
	comment: 'Description of the scene glass wallpaper.',
});
const IMAGE_DESCRIPTOR = msg({
	message: 'Your photo',
	comment: 'Glass theme wallpaper option: a picture the user picked. Keep it concise.',
});
const IMAGE_DESCRIPTION_DESCRIPTOR = msg({
	message: 'A picture from this device, tinted with your colours. Stays on this device.',
	comment: 'Description of the own-photo glass wallpaper.',
});
const CHOOSE_IMAGE_DESCRIPTOR = msg({message: 'Choose image…', comment: 'Button: pick a wallpaper picture.'});
const REPLACE_IMAGE_DESCRIPTOR = msg({message: 'Replace image…', comment: 'Button: pick another wallpaper picture.'});
const REMOVE_IMAGE_DESCRIPTOR = msg({message: 'Remove', comment: 'Button: remove the wallpaper picture.'});
const IMAGE_SAVING_DESCRIPTOR = msg({message: 'Saving…', comment: 'Wallpaper picture status while it is stored.'});
const IMAGE_STORED_DESCRIPTOR = msg({
	message: 'Stored on this device ({size}).',
	comment: 'Wallpaper picture status. {size} is like "1.2 MB".',
});
const IMAGE_NONE_DESCRIPTOR = msg({
	message: 'No picture yet. Pick one and it is shrunk to fit your screen.',
	comment: 'Wallpaper picture status when none is stored.',
});
const IMAGE_ERROR_UNSUPPORTED_DESCRIPTOR = msg({
	message: 'That file is not an image.',
	comment: 'Wallpaper picture error.',
});
const IMAGE_ERROR_TOO_LARGE_DESCRIPTOR = msg({
	message: 'That image is larger than 25 MB.',
	comment: 'Wallpaper picture error.',
});
const IMAGE_ERROR_STORAGE_DESCRIPTOR = msg({
	message: 'This browser does not allow the picture to be stored.',
	comment: 'Wallpaper picture error.',
});
const DAYLIGHT_DESCRIPTOR = msg({
	message: 'Follow the time of day',
	comment: 'Switch label: shift the wallpaper colours and brightness through the day.',
});
const DAYLIGHT_DESCRIPTION_DESCRIPTOR = msg({
	message: 'Warmer at dawn and dusk, brighter at midday, cooler and dimmer at night. Your colours stay the base.',
	comment: 'Description under the daylight wallpaper switch.',
});
const PLAIN_DESCRIPTOR = msg({
	message: 'Plain',
	comment: 'Glass theme wallpaper option: a flat dark colour. Keep it concise.',
});
const PLAIN_DESCRIPTION_DESCRIPTOR = msg({
	message: 'A flat neutral canvas with a hint of the first colour. The default and the cheapest to render.',
	comment: 'Description of the plain glass wallpaper.',
});
const FLOATING_PANELS_DESCRIPTOR = msg({
	message: 'Floating panels',
	comment: 'Switch label: show the sidebar and chat as separate rounded glass panels.',
});
const ANIMATE_WALLPAPER_DESCRIPTOR = msg({
	message: 'Animate wallpaper',
	comment: 'Switch label: slowly drift the wallpaper behind the glass panels.',
});
const ANIMATE_WALLPAPER_DESCRIPTION_DESCRIPTOR = msg({
	message: 'Slowly drift the wallpaper. Follows your reduced motion setting.',
	comment: 'Description under the animate wallpaper switch.',
});
const REDUCE_TRANSPARENCY_DESCRIPTOR = msg({
	message: 'Reduce transparency',
	comment: 'Switch label: make glass panels opaque and skip the blur.',
});
const RESET_GLASS_EFFECT_DESCRIPTOR = msg({
	message: 'Reset to default',
	comment: 'Button label that restores the glass theme settings. Keep it concise.',
});

function formatPercent(value: number): string {
	return `${Math.round(value)}%`;
}

function formatSigned(value: number): string {
	return value > 0 ? `+${Math.round(value)}` : `${Math.round(value)}`;
}

const LIGHT_LOOKS: ReadonlyArray<{value: GlassLook; name: MessageDescriptor; description: MessageDescriptor}> = [
	{value: GlassLooks.MIST_MONO, name: LOOK_MIST_MONO_DESCRIPTOR, description: LOOK_MIST_MONO_DESCRIPTION_DESCRIPTOR},
	{
		value: GlassLooks.MIST_STUDIO,
		name: LOOK_MIST_STUDIO_DESCRIPTOR,
		description: LOOK_MIST_STUDIO_DESCRIPTION_DESCRIPTOR,
	},
	{value: GlassLooks.MIST_KONVO, name: LOOK_MIST_KONVO_DESCRIPTOR, description: LOOK_MIST_KONVO_DESCRIPTION_DESCRIPTOR},
];
const DARK_LOOKS: ReadonlyArray<{value: GlassLook; name: MessageDescriptor; description: MessageDescriptor}> = [
	{
		value: GlassLooks.SMOKED_GRAPHITE,
		name: LOOK_SMOKED_GRAPHITE_DESCRIPTOR,
		description: LOOK_SMOKED_GRAPHITE_DESCRIPTION_DESCRIPTOR,
	},
	{
		value: GlassLooks.SMOKED_STUDIO,
		name: LOOK_SMOKED_STUDIO_DESCRIPTOR,
		description: LOOK_SMOKED_STUDIO_DESCRIPTION_DESCRIPTOR,
	},
];

/** KONVO: look cards for one tone; the selected card is the look remembered for that tone. */
const GlassLookCards: React.FC<{tone: 'light' | 'dark'; heading?: string}> = observer(({tone, heading}) => {
	const {i18n} = useLingui();
	const looks = tone === 'light' ? LIGHT_LOOKS : DARK_LOOKS;
	const usingPhoto = GlassTheme.backdrop === GlassBackdropStyles.IMAGE;
	const selected = usingPhoto ? null : tone === 'light' ? GlassTheme.lightLook : GlassTheme.darkLook;
	return (
		<div data-flx={`user.appearance-tab.glass-tab.looks.${tone}`}>
			{heading && <p className={choiceCardStyles.subheading}>{heading}</p>}
			<div
				className={clsx(choiceCardStyles.grid, styles.lookGrid)}
				role="radiogroup"
				aria-label={heading ?? i18n._(LOOKS_DESCRIPTOR)}
			>
				{looks.map((look) => (
					<ChoiceCard
						key={look.value}
						name={i18n._(look.name)}
						description={i18n._(look.description)}
						checked={selected === look.value}
						preview={
							<ThemePreviewArt
								variant={tone === 'light' ? 'glassLight' : 'glassDark'}
								wallpaper={GLASS_LOOK_VALUES[look.value].wallpaperColors}
							/>
						}
						onSelect={() => GlassThemeCommands.applyLook(look.value)}
						data-flx={`user.appearance-tab.glass-tab.look.${look.value}`}
					/>
				))}
				{tone === 'dark' && <PhotoLookCard />}
			</div>
		</div>
	);
});

async function pickWallpaperImage(): Promise<void> {
	const [file] = await openFilePicker({accept: 'image/*'});
	if (!file) return;
	const ok = await GlassWallpaperImage.setImage(file);
	if (ok) GlassThemeCommands.update({backdrop: GlassBackdropStyles.IMAGE});
}

/** KONVO round 6: "Your photo" look. Uses the stored picture, or opens the picker when there is none. */
const PhotoLookCard: React.FC = observer(() => {
	const {i18n} = useLingui();
	const checked = GlassTheme.backdrop === GlassBackdropStyles.IMAGE;
	const handleSelect = () => {
		if (GlassWallpaperImage.hasImage) {
			GlassThemeCommands.update({backdrop: GlassBackdropStyles.IMAGE});
			return;
		}
		void pickWallpaperImage();
	};
	return (
		<ChoiceCard
			name={i18n._(IMAGE_DESCRIPTOR)}
			description={i18n._(LOOK_PHOTO_DESCRIPTION_DESCRIPTOR)}
			checked={checked}
			disabled={GlassWallpaperImage.saving}
			preview={
				GlassWallpaperImage.url ? (
					<img className={styles.photoPreview} src={GlassWallpaperImage.url} alt="" />
				) : (
					<span className={clsx(styles.photoPreview, styles.photoPreviewEmpty)}>
						<ImageIcon size={22} />
						{i18n._(CHOOSE_A_PHOTO_DESCRIPTOR)}
					</span>
				)
			}
			onSelect={handleSelect}
			data-flx="user.appearance-tab.glass-tab.look.photo"
		/>
	);
});

const getLayoutOptions = (i18n: I18n): Array<RadioOption<GlassWallpaperLayout>> => [
	{
		value: GlassWallpaperLayouts.CORNERS,
		name: i18n._(LAYOUT_CORNERS_DESCRIPTOR),
		desc: i18n._(LAYOUT_CORNERS_DESCRIPTION_DESCRIPTOR),
	},
	{
		value: GlassWallpaperLayouts.STUDIO,
		name: i18n._(LAYOUT_STUDIO_DESCRIPTOR),
		desc: i18n._(LAYOUT_STUDIO_DESCRIPTION_DESCRIPTOR),
	},
	{
		value: GlassWallpaperLayouts.HORIZON,
		name: i18n._(LAYOUT_HORIZON_DESCRIPTOR),
		desc: i18n._(LAYOUT_HORIZON_DESCRIPTION_DESCRIPTOR),
	},
	{
		value: GlassWallpaperLayouts.CENTRE,
		name: i18n._(LAYOUT_CENTRE_DESCRIPTOR),
		desc: i18n._(LAYOUT_CENTRE_DESCRIPTION_DESCRIPTOR),
	},
];

const PALETTE_STUDIO_DESCRIPTOR = msg({
	message: 'Studio',
	comment: 'Glass wallpaper colour set: purple, teal, orange.',
});
const PALETTE_ORDER: ReadonlyArray<{value: GlassPalette; label: (i18n: I18n) => string}> = [
	{value: GlassPalettes.KONVO, label: (i18n) => i18n._(PALETTE_KONVO_DESCRIPTOR)},
	{value: GlassPalettes.STUDIO, label: (i18n) => i18n._(PALETTE_STUDIO_DESCRIPTOR)},
	{value: GlassPalettes.SUNRISE, label: (i18n) => i18n._(PALETTE_SUNRISE_DESCRIPTOR)},
	{value: GlassPalettes.OCEAN, label: (i18n) => i18n._(PALETTE_OCEAN_DESCRIPTOR)},
	{value: GlassPalettes.FOREST, label: (i18n) => i18n._(PALETTE_FOREST_DESCRIPTOR)},
	{value: GlassPalettes.EMBER, label: (i18n) => i18n._(PALETTE_EMBER_DESCRIPTOR)},
	{value: GlassPalettes.MONO, label: (i18n) => i18n._(PALETTE_MONO_DESCRIPTOR)},
];

function formatPixels(value: number): string {
	return `${Math.round(value)}px`;
}

const PRESET_ORDER: ReadonlyArray<{value: GlassPreset; label: (i18n: I18n) => string}> = [
	{value: GlassPresets.SMOKED, label: (i18n) => i18n._(SMOKED_DESCRIPTOR)},
	{value: GlassPresets.BALANCED, label: (i18n) => i18n._(BALANCED_DESCRIPTOR)},
	{value: GlassPresets.SUBTLE, label: (i18n) => i18n._(SUBTLE_DESCRIPTOR)},
	{value: GlassPresets.FROSTED, label: (i18n) => i18n._(FROSTED_DESCRIPTOR)},
	{value: GlassPresets.CLEAR, label: (i18n) => i18n._(CLEAR_DESCRIPTOR)},
];

const getBackdropOptions = (i18n: I18n): Array<RadioOption<GlassBackdropStyle>> => [
	{
		value: GlassBackdropStyles.FLAT,
		name: i18n._(PLAIN_DESCRIPTOR),
		desc: i18n._(PLAIN_DESCRIPTION_DESCRIPTOR),
	},
	{
		value: GlassBackdropStyles.PHOTO,
		name: i18n._(SCENE_DESCRIPTOR),
		desc: i18n._(SCENE_DESCRIPTION_DESCRIPTOR),
	},
	{
		value: GlassBackdropStyles.AURORA,
		name: i18n._(AURORA_DESCRIPTOR),
		desc: i18n._(AURORA_DESCRIPTION_DESCRIPTOR),
	},
	{
		value: GlassBackdropStyles.MESH,
		name: i18n._(MESH_DESCRIPTOR),
		desc: i18n._(MESH_DESCRIPTION_DESCRIPTOR),
	},
	{
		value: GlassBackdropStyles.IMAGE,
		name: i18n._(IMAGE_DESCRIPTOR),
		desc: i18n._(IMAGE_DESCRIPTION_DESCRIPTOR),
	},
];

function formatBytes(size: number): string {
	if (size < 1024 * 1024) return `${Math.max(1, Math.round(size / 1024))} KB`;
	return `${(size / (1024 * 1024)).toFixed(1)} MB`;
}

const WallpaperImageRow: React.FC = observer(() => {
	const {i18n} = useLingui();
	const pick = pickWallpaperImage;
	const status = GlassWallpaperImage.saving
		? i18n._(IMAGE_SAVING_DESCRIPTOR)
		: GlassWallpaperImage.error === 'unsupported'
			? i18n._(IMAGE_ERROR_UNSUPPORTED_DESCRIPTOR)
			: GlassWallpaperImage.error === 'too_large'
				? i18n._(IMAGE_ERROR_TOO_LARGE_DESCRIPTOR)
				: GlassWallpaperImage.error === 'storage'
					? i18n._(IMAGE_ERROR_STORAGE_DESCRIPTOR)
					: GlassWallpaperImage.hasImage
						? i18n._(IMAGE_STORED_DESCRIPTOR, {size: formatBytes(GlassWallpaperImage.size)})
						: i18n._(IMAGE_NONE_DESCRIPTOR);
	return (
		<div className={styles.imageRow} data-flx="user.appearance-tab.glass-tab.image-row">
			{GlassWallpaperImage.url ? (
				<img className={styles.imageThumb} src={GlassWallpaperImage.url} alt="" />
			) : (
				<span className={clsx(styles.imageThumb, styles.imageThumbEmpty)} />
			)}
			<div className={styles.imageText}>
				<p className={clsx(styles.sliderDescription, GlassWallpaperImage.error && styles.imageError)}>{status}</p>
				<div className={styles.presetRow}>
					<Button
						variant="secondary"
						small
						fitContent
						disabled={GlassWallpaperImage.saving}
						onClick={() => void pick()}
						data-flx="user.appearance-tab.glass-tab.button.choose-image"
					>
						{GlassWallpaperImage.hasImage ? i18n._(REPLACE_IMAGE_DESCRIPTOR) : i18n._(CHOOSE_IMAGE_DESCRIPTOR)}
					</Button>
					{GlassWallpaperImage.hasImage && (
						<Button
							variant="secondary"
							small
							fitContent
							onClick={() => void GlassWallpaperImage.clear()}
							data-flx="user.appearance-tab.glass-tab.button.remove-image"
						>
							{i18n._(REMOVE_IMAGE_DESCRIPTOR)}
						</Button>
					)}
				</div>
			</div>
		</div>
	);
});

interface GlassSliderProps {
	id: string;
	label: string;
	description?: string;
	value: number;
	defaultValue: number;
	min: number;
	max: number;
	format: (value: number) => string;
	onChange: (value: number) => void;
}

const GlassSlider: React.FC<GlassSliderProps> = ({
	id,
	label,
	description,
	value,
	defaultValue,
	min,
	max,
	format,
	onChange,
}) => {
	const {i18n} = useLingui();
	return (
		<div className={styles.sliderSection} data-flx="user.appearance-tab.glass-tab.slider-section">
			<div className={styles.sliderTitleRow} data-flx="user.appearance-tab.glass-tab.slider-title-row">
				<label htmlFor={id} className={styles.sliderLabel} data-flx="user.appearance-tab.glass-tab.slider-label">
					{label}
				</label>
				<SliderResetIconButton
					canReset={canResetSliderValue(value, defaultValue)}
					onReset={() => onChange(defaultValue)}
					ariaLabel={i18n._(RESET_SLIDER_TO_DEFAULT_VALUE_DESCRIPTOR)}
					dataFlx={`user.appearance-tab.glass-tab.reset-button.${id}`}
					data-flx="user.appearance-tab.glass-tab.slider-reset-icon-button"
				/>
			</div>
			{description ? (
				<p className={styles.sliderDescription} data-flx="user.appearance-tab.glass-tab.slider-description">
					{description}
				</p>
			) : null}
			<Slider
				value={value}
				defaultValue={value}
				factoryDefaultValue={defaultValue}
				minValue={min}
				maxValue={max}
				step={1}
				stickToMarkers={false}
				onValueRender={format}
				onValueChange={onChange}
				data-flx={`user.appearance-tab.glass-tab.slider.${id}`}
			/>
		</div>
	);
};

export function canResetGlassEffect(): boolean {
	return !GlassTheme.isDefault;
}

export const GlassEffectResetAction: React.FC = observer(() => {
	const {i18n} = useLingui();
	return (
		<Button
			variant="secondary"
			small
			fitContent
			onClick={() => GlassThemeCommands.reset()}
			data-flx="user.appearance-tab.glass-tab.button.reset"
		>
			{i18n._(RESET_GLASS_EFFECT_DESCRIPTOR)}
		</Button>
	);
});

/** Whether the detailed wallpaper and panel knobs are showing. Shared by the header button and the content. */
const customiseState = observable({open: false});
const setCustomiseOpen = action((open: boolean) => {
	customiseState.open = open;
});

/** KONVO round 6: tonal "Customise wallpaper" button that sits beside the Glass heading. */
export const GlassCustomiseAction: React.FC = observer(() => {
	const {i18n} = useLingui();
	if (!isGlassThemeActive()) return null;
	return (
		<Button
			variant="secondary"
			small
			fitContent
			leftIcon={<PaintBrushIcon size={16} />}
			aria-expanded={customiseState.open}
			onClick={() => setCustomiseOpen(!customiseState.open)}
			data-flx="user.appearance-tab.glass-tab.button.customise"
		>
			{i18n._(customiseState.open ? HIDE_CUSTOMISE_DESCRIPTOR : CUSTOMISE_DESCRIPTOR)}
		</Button>
	);
});

/** KONVO round 6: the three everyday glass knobs as grouped rows (Blur, Floating panels, Reduce transparency). */
export const GlassFineTuneContent: React.FC = observer(() => {
	const {i18n} = useLingui();
	const toggleFloating = () => GlassThemeCommands.update({floatingPanels: !GlassTheme.floatingPanels});
	const toggleReduce = () => GlassThemeCommands.update({reduceTransparency: !GlassTheme.reduceTransparency});
	return (
		<div className={styles.rows} data-flx="user.appearance-tab.glass-tab.fine-tune">
			<SettingsRow
				icon={<SlidersHorizontalIcon size={20} />}
				title={i18n._(BLUR_DESCRIPTOR)}
				description={i18n._(FINE_TUNE_BLUR_DESCRIPTION_DESCRIPTOR)}
				disabled={GlassTheme.reduceTransparency}
				control={({labelId}) => (
					<span className={styles.rowSlider}>
						<Slider
							value={GlassTheme.blur}
							defaultValue={GlassTheme.blur}
							factoryDefaultValue={GLASS_DEFAULT_SETTINGS.blur}
							minValue={GLASS_LIMITS.blur.min}
							maxValue={GLASS_LIMITS.blur.max}
							step={1}
							disabled={GlassTheme.reduceTransparency}
							ariaLabelledBy={labelId}
							ariaValueText={formatPixels(GlassTheme.blur)}
							onValueRender={formatPixels}
							onValueChange={(value) => GlassThemeCommands.update({blur: value})}
							data-flx="user.appearance-tab.glass-tab.slider.fine-tune-blur"
						/>
						<span className={styles.rowValue} aria-hidden="true">
							{Math.round(GlassTheme.blur)}
						</span>
					</span>
				)}
				data-flx="user.appearance-tab.glass-tab.row.blur"
			/>
			<SettingsRow
				icon={<SidebarSimpleIcon size={20} />}
				title={i18n._(FLOATING_PANELS_DESCRIPTOR)}
				description={i18n._(FINE_TUNE_FLOATING_DESCRIPTION_DESCRIPTOR)}
				onActivate={toggleFloating}
				control={({labelId, descriptionId}) => (
					<Switch
						compact
						value={GlassTheme.floatingPanels}
						onChange={(value) => GlassThemeCommands.update({floatingPanels: value})}
						ariaLabelledBy={labelId}
						ariaDescribedBy={descriptionId}
						data-flx="user.appearance-tab.glass-tab.switch.floating-panels"
					/>
				)}
				data-flx="user.appearance-tab.glass-tab.row.floating-panels"
			/>
			<SettingsRow
				icon={<ImageIcon size={20} />}
				title={i18n._(REDUCE_TRANSPARENCY_DESCRIPTOR)}
				description={i18n._(FINE_TUNE_REDUCE_DESCRIPTION_DESCRIPTOR)}
				onActivate={toggleReduce}
				control={({labelId, descriptionId}) => (
					<Switch
						compact
						value={GlassTheme.reduceTransparency}
						onChange={(value) => GlassThemeCommands.update({reduceTransparency: value})}
						ariaLabelledBy={labelId}
						ariaDescribedBy={descriptionId}
						data-flx="user.appearance-tab.glass-tab.switch.reduce-transparency"
					/>
				)}
				data-flx="user.appearance-tab.glass-tab.row.reduce-transparency"
			/>
		</div>
	);
});

export const GlassTabContent: React.FC = observer(() => {
	const {i18n} = useLingui();
	const customiseOpen = customiseState.open;
	useEffect(() => () => setCustomiseOpen(false), []);
	const isGlassTheme = isGlassThemeActive();
	const activePreset = GlassTheme.activePreset;
	if (!isGlassTheme) {
		return (
			<p className={styles.inactiveNote} data-flx="user.appearance-tab.glass-tab.inactive-note">
				{i18n._(GLASS_INACTIVE_NOTE_DESCRIPTOR)}
			</p>
		);
	}
	const activePalette = GlassTheme.activePalette;
	const setColor = (index: 0 | 1 | 2, value: string) => {
		const colors: [string, string, string] = [...GlassTheme.wallpaperColors] as [string, string, string];
		colors[index] = value;
		GlassThemeCommands.update({wallpaperColors: colors});
	};
	return (
		<div className={styles.root} data-flx="user.appearance-tab.glass-tab.root">
			<Segmented<GlassTone>
				value={GlassTheme.tone}
				onChange={(value) => GlassThemeCommands.setTone(value)}
				aria-label={i18n._(TONE_DESCRIPTOR)}
				className={styles.toneRow}
				options={[
					{value: GlassTones.LIGHT, children: i18n._(TONE_LIGHT_DESCRIPTOR), icon: <SunIcon size={16} />},
					{value: GlassTones.DARK, children: i18n._(TONE_DARK_DESCRIPTOR), icon: <MoonIcon size={16} />},
					{value: GlassTones.SYSTEM, children: i18n._(MATCH_SYSTEM_DESCRIPTOR), icon: <DesktopIcon size={16} />},
				]}
				data-flx="user.appearance-tab.glass-tab.tone"
			/>
			{GlassTheme.tone === GlassTones.SYSTEM ? (
				<>
					<GlassLookCards tone="light" heading={i18n._(LIGHT_LOOK_DESCRIPTOR)} />
					<GlassLookCards tone="dark" heading={i18n._(DARK_LOOK_DESCRIPTOR)} />
				</>
			) : (
				<GlassLookCards tone={GlassTheme.tone === GlassTones.LIGHT ? 'light' : 'dark'} />
			)}
			{customiseOpen && (
				<div className={styles.customise} data-flx="user.appearance-tab.glass-tab.customise">
					<div className={styles.sliderSection} data-flx="user.appearance-tab.glass-tab.preset-section">
						<p className={styles.groupTitle} data-flx="user.appearance-tab.glass-tab.preset-title">
							{i18n._(PRESET_DESCRIPTOR)}
						</p>
						<div
							className={styles.presetRow}
							role="group"
							aria-label={i18n._(PRESET_DESCRIPTOR)}
							data-flx="user.appearance-tab.glass-tab.preset-row"
						>
							{PRESET_ORDER.map((preset) => (
								<button
									key={preset.value}
									type="button"
									className={clsx(styles.presetButton, activePreset === preset.value && styles.presetButtonActive)}
									aria-pressed={activePreset === preset.value}
									onClick={() => GlassThemeCommands.applyPreset(preset.value)}
									data-flx={`user.appearance-tab.glass-tab.preset-button.${preset.value}`}
								>
									{preset.label(i18n)}
								</button>
							))}
						</div>
					</div>
					<GlassSlider
						id="glass-opacity"
						label={i18n._(PANEL_OPACITY_DESCRIPTOR)}
						description={i18n._(PANEL_OPACITY_DESCRIPTION_DESCRIPTOR)}
						value={GlassTheme.surfaceOpacity}
						defaultValue={GLASS_DEFAULT_SETTINGS.surfaceOpacity}
						min={GLASS_LIMITS.surfaceOpacity.min}
						max={GLASS_LIMITS.surfaceOpacity.max}
						format={formatRoundedPercentage}
						onChange={(value) => GlassThemeCommands.update({surfaceOpacity: value})}
					/>
					<GlassSlider
						id="glass-edge-glow"
						label={i18n._(EDGE_GLOW_DESCRIPTOR)}
						value={GlassTheme.borderGlow}
						defaultValue={GLASS_DEFAULT_SETTINGS.borderGlow}
						min={GLASS_LIMITS.borderGlow.min}
						max={GLASS_LIMITS.borderGlow.max}
						format={formatRoundedPercentage}
						onChange={(value) => GlassThemeCommands.update({borderGlow: value})}
					/>
					<GlassSlider
						id="glass-corner-radius"
						label={i18n._(CORNER_RADIUS_DESCRIPTOR)}
						description={i18n._(CORNER_RADIUS_DESCRIPTION_DESCRIPTOR)}
						value={GlassTheme.cornerRadius}
						defaultValue={GLASS_DEFAULT_SETTINGS.cornerRadius}
						min={GLASS_LIMITS.cornerRadius.min}
						max={GLASS_LIMITS.cornerRadius.max}
						format={formatPixels}
						onChange={(value) => GlassThemeCommands.update({cornerRadius: value})}
					/>
					<div className={styles.sliderSection} data-flx="user.appearance-tab.glass-tab.wallpaper-section">
						<p className={styles.groupTitle} data-flx="user.appearance-tab.glass-tab.wallpaper-title">
							{i18n._(WALLPAPER_DESCRIPTOR)}
						</p>
						<RadioGroup
							options={getBackdropOptions(i18n)}
							value={GlassTheme.backdrop}
							onChange={(value) => GlassThemeCommands.update({backdrop: value})}
							data-flx="user.appearance-tab.glass-tab.radio-group.wallpaper"
						/>
						{GlassTheme.backdrop === GlassBackdropStyles.IMAGE && <WallpaperImageRow />}
					</div>
					<div className={styles.sliderSection} data-flx="user.appearance-tab.glass-tab.layout-section">
						<p className={styles.groupTitle} data-flx="user.appearance-tab.glass-tab.layout-title">
							{i18n._(LAYOUT_DESCRIPTOR)}
						</p>
						<RadioGroup
							options={getLayoutOptions(i18n)}
							value={GlassTheme.wallpaperLayout}
							onChange={(value) => GlassThemeCommands.update({wallpaperLayout: value})}
							data-flx="user.appearance-tab.glass-tab.radio-group.layout"
						/>
					</div>
					<div className={styles.sliderSection} data-flx="user.appearance-tab.glass-tab.colours-section">
						<p className={styles.groupTitle} data-flx="user.appearance-tab.glass-tab.colours-title">
							{i18n._(COLOURS_DESCRIPTOR)}
						</p>
						<p className={styles.sliderDescription} data-flx="user.appearance-tab.glass-tab.colours-description">
							{i18n._(COLOURS_DESCRIPTION_DESCRIPTOR)}
						</p>
						<div className={styles.presetRow} role="group" data-flx="user.appearance-tab.glass-tab.palette-row">
							{PALETTE_ORDER.map((palette) => (
								<button
									key={palette.value}
									type="button"
									className={clsx(
										styles.presetButton,
										styles.paletteButton,
										activePalette === palette.value && styles.presetButtonActive,
									)}
									aria-pressed={activePalette === palette.value}
									onClick={() => GlassThemeCommands.applyPalette(palette.value)}
									data-flx={`user.appearance-tab.glass-tab.palette.${palette.value}`}
								>
									<span className={styles.paletteDots} aria-hidden="true">
										{GLASS_PALETTE_COLORS[palette.value].map((color, index) => (
											<span key={index} className={styles.paletteDot} style={{background: color}} />
										))}
									</span>
									{palette.label(i18n)}
								</button>
							))}
						</div>
						<div className={styles.colourRow} data-flx="user.appearance-tab.glass-tab.colour-row">
							{GlassTheme.wallpaperColors.map((color, index) => (
								<label key={index} className={styles.colourSwatch} style={{background: color}}>
									<input
										type="color"
										value={color}
										aria-label={i18n._(COLOUR_PICKER_DESCRIPTOR, {index: index + 1})}
										onChange={(event) => setColor(index as 0 | 1 | 2, event.target.value)}
										data-flx={`user.appearance-tab.glass-tab.colour.${index + 1}`}
									/>
								</label>
							))}
						</div>
					</div>
					<GlassSlider
						id="wallpaper-intensity"
						label={i18n._(INTENSITY_DESCRIPTOR)}
						description={i18n._(INTENSITY_DESCRIPTION_DESCRIPTOR)}
						value={GlassTheme.wallpaperIntensity}
						defaultValue={GLASS_DEFAULT_SETTINGS.wallpaperIntensity}
						min={GLASS_LIMITS.wallpaperIntensity.min}
						max={GLASS_LIMITS.wallpaperIntensity.max}
						format={formatPercent}
						onChange={(value) => GlassThemeCommands.update({wallpaperIntensity: value})}
					/>
					<GlassSlider
						id="wallpaper-softness"
						label={i18n._(SOFTNESS_DESCRIPTOR)}
						description={i18n._(SOFTNESS_DESCRIPTION_DESCRIPTOR)}
						value={GlassTheme.wallpaperSoftness}
						defaultValue={GLASS_DEFAULT_SETTINGS.wallpaperSoftness}
						min={GLASS_LIMITS.wallpaperSoftness.min}
						max={GLASS_LIMITS.wallpaperSoftness.max}
						format={formatPercent}
						onChange={(value) => GlassThemeCommands.update({wallpaperSoftness: value})}
					/>
					<GlassSlider
						id="wallpaper-brightness"
						label={i18n._(BRIGHTNESS_DESCRIPTOR)}
						description={i18n._(BRIGHTNESS_DESCRIPTION_DESCRIPTOR)}
						value={GlassTheme.wallpaperBrightness}
						defaultValue={GLASS_DEFAULT_SETTINGS.wallpaperBrightness}
						min={GLASS_LIMITS.wallpaperBrightness.min}
						max={GLASS_LIMITS.wallpaperBrightness.max}
						format={formatSigned}
						onChange={(value) => GlassThemeCommands.update({wallpaperBrightness: value})}
					/>
					<Switch
						label={i18n._(DAYLIGHT_DESCRIPTOR)}
						description={i18n._(DAYLIGHT_DESCRIPTION_DESCRIPTOR)}
						value={GlassTheme.wallpaperDaylight}
						onChange={(value) => GlassThemeCommands.update({wallpaperDaylight: value})}
						data-flx="user.appearance-tab.glass-tab.switch.daylight"
					/>
					<GlassSlider
						id="wallpaper-grain"
						label={i18n._(GRAIN_DESCRIPTOR)}
						description={i18n._(GRAIN_DESCRIPTION_DESCRIPTOR)}
						value={GlassTheme.wallpaperGrain}
						defaultValue={GLASS_DEFAULT_SETTINGS.wallpaperGrain}
						min={GLASS_LIMITS.wallpaperGrain.min}
						max={GLASS_LIMITS.wallpaperGrain.max}
						format={formatPercent}
						onChange={(value) => GlassThemeCommands.update({wallpaperGrain: value})}
					/>
					<GlassSlider
						id="wallpaper-vignette"
						label={i18n._(VIGNETTE_DESCRIPTOR)}
						description={i18n._(VIGNETTE_DESCRIPTION_DESCRIPTOR)}
						value={GlassTheme.wallpaperVignette}
						defaultValue={GLASS_DEFAULT_SETTINGS.wallpaperVignette}
						min={GLASS_LIMITS.wallpaperVignette.min}
						max={GLASS_LIMITS.wallpaperVignette.max}
						format={formatPercent}
						onChange={(value) => GlassThemeCommands.update({wallpaperVignette: value})}
					/>
					<Switch
						label={i18n._(ANIMATE_WALLPAPER_DESCRIPTOR)}
						description={i18n._(ANIMATE_WALLPAPER_DESCRIPTION_DESCRIPTOR)}
						value={GlassTheme.animateBackdrop}
						onChange={(value) => GlassThemeCommands.update({animateBackdrop: value})}
						data-flx="user.appearance-tab.glass-tab.switch.animate-wallpaper"
					/>
				</div>
			)}
		</div>
	);
});
